import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import useCostCenterService from '../../services/useCostCenterService';
import showToast from '../../utils/toast';
import '../Services/Adicional/AdicionalCatalogModal.css';

export interface QuickCreatedCostCenter {
  id: number;
  code: string;
  company: number;
}

interface CostCenterQuickAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  onCreated: (costCenter: QuickCreatedCostCenter) => void;
}

/**
 * Helper para crear un centro de costo sin salir del formulario de servicios (paso 3).
 */
const CostCenterQuickAddModal: React.FC<CostCenterQuickAddModalProps> = ({
  isOpen,
  onClose,
  companyId,
  onCreated,
}) => {
  const { store, fetching } = useCostCenterService();
  const [code, setCode] = useState('');
  const loading = Boolean(fetching.store);

  useEffect(() => {
    if (isOpen) {
      setCode('');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !loading) onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, loading]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed || !companyId || loading) return;

    store(
      { code: trimmed, company: companyId },
      {
        onSuccess: (r: any) => {
          const id = r?.data?.id;
          if (typeof id !== 'number') {
            showToast(
              'El centro de costo se creó pero no se pudo seleccionar automáticamente',
              'info'
            );
            onClose();
            return;
          }
          showToast(r?.message ?? 'Centro de costo creado correctamente', 'success');
          onCreated({
            id,
            code: String(r?.data?.code ?? trimmed),
            company: Number(r?.data?.company ?? companyId),
          });
        },
        onError: (r: any) => {
          const errors = r?.errors ?? r?.data?.errors ?? r?.data;
          const codeErr =
            typeof errors === 'object' && errors?.code
              ? Array.isArray(errors.code)
                ? errors.code[0]
                : errors.code
              : null;
          const msg =
            (typeof codeErr === 'string' && codeErr.trim()) ||
            (typeof r?.message === 'string' && r.message.trim()) ||
            'No se pudo crear el centro de costo';
          showToast(String(msg), 'error');
        },
      }
    );
  };

  if (!isOpen) return null;

  return (
    <div className="adicional-modal-overlay" onClick={loading ? undefined : onClose}>
      <div className="adicional-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="adicional-modal-header">
          <h3 className="adicional-modal-title">Nuevo centro de costo</h3>
          <button
            type="button"
            className="adicional-modal-close"
            onClick={onClose}
            disabled={loading}
            aria-label="Cerrar"
          >
            <X size={22} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="adicional-modal-form">
          <div className="adicional-modal-field">
            <label htmlFor="qa-cc-code">Código</label>
            <input
              id="qa-cc-code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Ej. CC-001"
              required
              autoFocus
              disabled={loading}
              maxLength={50}
            />
          </div>
          <div className="adicional-modal-actions">
            <button
              type="button"
              className="adicional-modal-btn secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="adicional-modal-btn primary"
              disabled={loading || !code.trim()}
            >
              {loading ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CostCenterQuickAddModal;
